import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useGetDistinctTitlesQuery,
  useCampaignTitlesControllerGetCampaignTitlesQuery,
  useCampaignConfigurationControllerGetQuery,
  useCampaignConfigurationControllerCreateMutation,
  useCampaignConfigurationControllerUpdateMutation,
  useCampaignConfigurationControllerDeleteMutation,
  useCampaignsControllerUpdateMutation,
  type GtiBlocklistConfigValue,
} from 'src/store/leela-api';
import {
  type CampaignDesiredStateDto,
  type CampaignTitlesControllerGetCampaignTitlesApiArg,
  type CampaignTitle,
} from 'src/store/generated/leela-api-generated';
import { useToasterContext } from 'src/components/common/alerts/ToasterContext';
import { AlertType } from 'src/components/common/alerts/alert.types';
import { useTranslation } from 'src/arb-wrapper';
import { useCanPerform } from 'src/hooks/useCanPerform';
import { createExclusionList, addToExclusionList, removeFromExclusionList } from 'src/utils/exclusionListUtils';
import SearchField from '@amzn/meridian/search-field';
import Column from '@amzn/meridian/column';
import { TitlesTable, type TitleInfo } from 'src/components/titles/TitlesTable';
import { buildStrategyLabelMap, resolveStrategyLabel } from 'src/utils/strategyLabels';
import { epochToIsoDate } from 'src/utils/releaseDate';

interface CampaignTitlesContainerProps {
  campaignId: string;
  campaign: CampaignDesiredStateDto;
}

export const CampaignTitlesContainer: React.FC<CampaignTitlesContainerProps> = ({
  campaignId,
  campaign,
}) => {
  const { t } = useTranslation();
  const { createToast } = useToasterContext();
  const canManageCampaigns = useCanPerform('MANAGE_CAMPAIGNS');
  const firstStrategy = campaign.productStrategies?.[0];

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [mutatingGtis, setMutatingGtis] = useState<Set<string>>(new Set());

  // Debounce search input — reset pagination when search changes
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(1);
    }, 300);
    return () => { clearTimeout(timer); };
  }, [searchInput]);

  // Exclusion list hooks
  const { data: exclusionListConfig, isLoading: isExclusionListLoading } =
    useCampaignConfigurationControllerGetQuery(
      { id: campaign.exclusionListId ?? '' },
      { skip: !campaign.exclusionListId },
    );
  const [createConfig] = useCampaignConfigurationControllerCreateMutation();
  const [updateConfig] = useCampaignConfigurationControllerUpdateMutation();
  const [deleteConfig] = useCampaignConfigurationControllerDeleteMutation();
  const [updateCampaign] = useCampaignsControllerUpdateMutation();

  const excludedGtis = useMemo(() => {
    if (!exclusionListConfig) return new Set<string>();
    const config = exclusionListConfig.config as GtiBlocklistConfigValue;
    return new Set(config?.gtis ?? []);
  }, [exclusionListConfig]);

  const addMutatingGti = useCallback((gti: string) => {
    setMutatingGtis((prev) => new Set(prev).add(gti));
  }, []);

  const removeMutatingGti = useCallback((gti: string) => {
    setMutatingGtis((prev) => {
      const next = new Set(prev);
      next.delete(gti);
      return next;
    });
  }, []);

  const onExclude = useCallback(async (gti: string) => {
    if (mutatingGtis.has(gti)) return;
    addMutatingGti(gti);
    try {
      if (!campaign.exclusionListId) {
        await createExclusionList(gti, campaignId, campaign, createConfig, updateCampaign, deleteConfig);
      } else {
        const currentGtis = (exclusionListConfig?.config as GtiBlocklistConfigValue)?.gtis ?? [];
        await addToExclusionList(gti, campaign.exclusionListId, currentGtis, updateConfig);
      }
    } catch {
      createToast({ type: AlertType.ERROR, message: t('campaign_titles_exclude_error') });
    } finally {
      removeMutatingGti(gti);
    }
  }, [campaign, campaignId, exclusionListConfig, mutatingGtis, addMutatingGti, removeMutatingGti, createConfig, updateConfig, updateCampaign, deleteConfig, createToast, t]);

  const onInclude = useCallback(async (gti: string) => {
    if (mutatingGtis.has(gti) || !campaign.exclusionListId) return;
    addMutatingGti(gti);
    try {
      const currentGtis = (exclusionListConfig?.config as GtiBlocklistConfigValue)?.gtis ?? [];
      await removeFromExclusionList(gti, campaign.exclusionListId, currentGtis, updateConfig);
    } catch {
      createToast({ type: AlertType.ERROR, message: t('campaign_titles_include_error') });
    } finally {
      removeMutatingGti(gti);
    }
  }, [campaign.exclusionListId, exclusionListConfig, mutatingGtis, addMutatingGti, removeMutatingGti, updateConfig, createToast, t]);

  // Step 1: Fetch paginated distinct titles for this campaign via the aggregation endpoint
  const { data, isLoading, isFetching, error } = useGetDistinctTitlesQuery({
    campaignIds: [campaignId],
    sort: ['name:1'],
    pageSize,
    page,
    search: debouncedSearch
      ? Array.from(new TextEncoder().encode(debouncedSearch), (b) => b.toString(16).padStart(2, '0')).join('')
      : undefined,
  });

  // Step 2: Enrich with metadata from campaign-titles service
  const gtisForEnrichment = useMemo(
    () => (data?.data ?? []).map((item) => item.gti).filter(Boolean),
    [data],
  );

  const firstLocale = firstStrategy?.locales?.[0];

  const campaignTitlesQueryArgs: CampaignTitlesControllerGetCampaignTitlesApiArg | undefined = useMemo(() => {
    if (gtisForEnrichment.length === 0 || !firstStrategy?.country || !firstLocale) {
      return undefined;
    }
    return {
      gtiList: gtisForEnrichment,
      countryCode: firstStrategy.country,
      locale: firstLocale,
      ...(firstStrategy.businessLine && { businessLine: firstStrategy.businessLine }),
    };
  }, [gtisForEnrichment, firstStrategy?.country, firstStrategy?.businessLine, firstLocale]);

  const { data: campaignTitles, isLoading: isLoadingTitles } =
    useCampaignTitlesControllerGetCampaignTitlesQuery(
      campaignTitlesQueryArgs!,
      { skip: campaignTitlesQueryArgs === undefined },
    );

  // productStrategyId → display name for this campaign's strategies.
  const strategyLabels = useMemo(() => buildStrategyLabelMap([campaign]), [campaign]);

  // Step 3: Merge aggregation data with enriched metadata
  const titles: TitleInfo[] = useMemo(() => {
    const baseTitles = data?.data ?? [];
    const metadataMap = new Map<string, CampaignTitle>(
      (campaignTitles ?? []).map((ct) => [ct.gti, ct]),
    );

    return baseTitles.map((title) => {
      const meta = metadataMap.get(title.gti);
      return {
        gti: title.gti,
        name: meta?.title ?? title.name,
        description: meta?.description ?? undefined,
        genres: meta?.genres ?? undefined,
        coverImageUrl: meta?.coverImageUrl ?? undefined,
        releaseDate: epochToIsoDate(meta?.publicReleaseDate),
        strategyNames: (title.productStrategyIds ?? []).map((id) => resolveStrategyLabel(strategyLabels, id)),
      };
    });
  }, [data, campaignTitles, strategyLabels]);

  const hasMore = data?.hasMore ?? false;

  return (
    <Column spacing="400">
      <SearchField
        data-testid="campaign-titles-search"
        placeholder={t('search_titles')}
        size="small"
        value={searchInput}
        width={300}
        onChange={(value: string) => { setSearchInput(value); }}
        onSubmit={() => { setDebouncedSearch(searchInput); setPage(1); }}
      />
      <TitlesTable
      campaignId={campaignId}
      cursorPagination={{
        page,
        pageSize,
        hasMore,
        isLoading: isFetching,
        onNextPage: () => {
          if (hasMore) {
            setPage((p) => p + 1);
          }
        },
        onPreviousPage: () => {
          setPage((p) => Math.max(1, p - 1));
        },
        onPageSizeChange: (size) => {
          setPageSize(size);
          setPage(1);
        },
      }}
      exclusionList={canManageCampaigns
        ? {
            excludedGtis,
            isLoading: isExclusionListLoading,
            onExclude,
            onInclude,
            mutatingGtis,
          }
        : undefined}
      hasError={!!error}
      hideCampaigns
      isEnriching={isLoadingTitles}
      isFetching={isFetching}
      isLoading={isLoading}
      showStrategies
      titles={titles}
    />
    </Column>
  );
};
